"use client";

import Link from "next/link";
import { useState } from "react";
import FAIcon from "@/components/icons/faIcon";

export default function LocationCoverageMap({ map }) {
  const [active, setActive] = useState(null);

  if (!map?.query) {
    return null;
  }

  const {
    className = "",
    frameClassName = "",
    iframeClassName = "",
    showLinks = true,
    inactiveZoom = 10,
    activeZoom = 12,
  } = map;

  const currentLocation = active ?? {
    name: map.name,
    query: map.query,
  };
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(currentLocation.query)}&t=&z=${active ? activeZoom : inactiveZoom}&ie=UTF8&iwloc=&output=embed`;
  const links = map.links ?? [];
  const rootClassName = ["flex flex-col gap-4", className]
    .filter(Boolean)
    .join(" ");
  const mapShellClassName = [
    "overflow-hidden rounded-[1.75rem] border border-[#06040c]/10 bg-white shadow-[0_12px_40px_rgba(6,4,12,0.06)]",
    frameClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const mapFrameClassName = ["h-96 w-full sm:h-[480px]", iframeClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName}>
      <div className={mapShellClassName}>
        <iframe
          key={mapSrc}
          src={mapSrc}
          width="100%"
          height="100%"
          className={mapFrameClassName}
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={`Map showing ${currentLocation.name}`}
        />
      </div>

      {showLinks && links.length ? (
        <div className="rounded-[1.5rem] border border-[#06040c]/8 bg-white px-5 py-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-[#06040c]/42">
              {links.length > 6 ? "Towns and areas" : "Nearby coverage"}
            </p>
            {active ? (
              <button
                type="button"
                onClick={() => setActive(null)}
                className="group inline-flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#06040c]/42 transition-colors duration-200 hover:text-[#06040c]"
              >
                <FAIcon
                  icon="arrow-left"
                  size={9}
                  noMargin
                  className="transition-transform duration-200 group-hover:-translate-x-0.5"
                />
                Back to {map.name}
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {links.map((item) => {
              const isActive = active?.name === item.name;

              return (
                <Link
                  key={`${item.href}-${item.name}`}
                  href={item.href}
                  title={`Open ${item.name} page`}
                  onClick={() => setActive(isActive ? null : item)}
                  className={`group/chip inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-200 ${
                    isActive
                      ? "bg-[#06040c] text-white shadow-sm"
                      : "border border-[#06040c]/10 bg-neutral-50 text-[#06040c]/60 hover:border-[#06040c]/20 hover:bg-white hover:text-[#06040c] hover:shadow-sm"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-200 ${
                      isActive
                        ? "bg-primary"
                        : "bg-[#06040c]/12 group-hover/chip:bg-[#06040c]/30"
                    }`}
                  />
                  <span className="whitespace-nowrap">{item.name}</span>
                  <span
                    className={`flex items-center justify-center rounded-md p-0.5 transition-all duration-200 ${
                      isActive
                        ? "bg-white/15 text-white/70 group-hover/chip:bg-white/25 group-hover/chip:text-white"
                        : "bg-[#06040c]/6 text-[#06040c]/30 group-hover/chip:bg-[#06040c]/10 group-hover/chip:text-[#06040c]/60"
                    }`}
                  >
                    <FAIcon icon="arrow-right" size={9} noMargin />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
